enum ReportsViewBy {
  ADVISOR
  LENDER
  CASE_TYPE
  INTRODUCER
  YEAR_MONTH
  LEDGER_TYPE
}

enum ReportsViewDateBy {
  CASE_CREATED_AT
  LEDGER_CREATED_AT
  RECEIVED_DATE
}

enum ReportsPaymentStatus {
  OUTSTANDING
  COLLECTED
  TOTAL_REVENUE
}

enum RevenueReportsType {
  REVENUE
  PREDICTED_REVENUE
}

enum ReportCategory {
  GENERAL
  RMA
  DATASECURITY
}

type ReportColum {
  name: String
  display_name: String
}

type ReportEntity {
  description: String!
  filters: [FilterSchema!]
  id: String
  title: String
  endpoint: String
  columns: [ReportColum!]
  is_xls: Boolean
  category: ReportCategory
}

type ReportsCurrentPipelineResponse {
  filters: ReportsCaseStatsFilter
  grouped_results: [ReportsGroupedCaseStats!]
  totals: [ReportsCaseStats!]
}

type ReportsCancellationReasonResponse {
  filters: ReportsCaseStatsFilter
  totals: [ReportsCancellationTotal!]
  grouped_totals: [ReportsAdvisorTotal!]
}

type ReportsCaseWaterfallResponse {
  filters: ReportsCaseStatsFilter
  grouped_results: [ReportsGroupedCaseStats!]
  totals: [ReportsCaseStats!]
}

type ReportsCaseWaterfallIntroducerResponse {
  filters: ReportsCaseStatsFilter
  grouped_results: [ReportsGroupedCaseStats!]
  totals: [ReportsCaseStats!]
}

type ReportsCaseStats {
  case_status: String
  count: Int
  value: Float
}

type ReportsAdvisorTotal {
  group_id: String
  results: [AdvisorTotalResults!]
}

type AdvisorTotalResults {
  cancellation_reason: String
  count: Int
  value: Float
}

type ReportsCancellationTotal {
  cancellation_reason: String
  count: Int
  value: Float
}

type ReportsCaseStatsFilter {
  advisor_ids: [String!]
  case_types: [String!]
  case_status: [String!]
  introducer_ids: [String!]
  lender_ids: [String!]
  start_date: String
  org_id: String
  end_date: String
  ledger_type: [LedgerType!]
  view_by: ReportsViewBy
}

type ReportsGroupedCaseStats {
  group_id: String
  results: [ReportsCaseStats!]
}

type ReportsCommissionsResponse {
  summaries: [ReportsCommissionsSummaries!]
}

type ReportsCommissionsSummaries {
  fees: Int
  from: Int
  percentage: Int
  period_id: String
  period_name: String
  to: Int
  total_commission: Int
  internalHash: String
}

type ReportsRevenueFilters {
  advisor_ids: [String!]
  case_status: [String!]
  case_types: [String!]
  end_date: String
  introducer_ids: [String!]
  lender_ids: [String!]
  payment_status: ReportsPaymentStatus
  ledger_type: [LedgerType!]
  start_date: String
  view_by: ReportsViewBy
  view_by_date: ReportsViewDateBy
}

type ReportsRevenueRow {
  group_id: String!
  value: Float!
  year_month: String!
}

type ReportsRevenueTotals {
  by_group_id: [ReportsRevenueTotalsItem!]
  by_year_month: [ReportsRevenueTotalsItem!]
}

type ReportsRevenueTotalsItem {
  id: String
  value: String
}

type ReportsRevenueResponse {
  filters: ReportsRevenueFilters!
  results: [ReportsRevenueRow!]
  totals: ReportsRevenueTotals!
}

type ReportsReportQueueResponse {
  reports: [ReportsResponse]
}

type ReportsResponse {
  report_id: String
  name: String
  description: String
  created_at: String
  key: String
  status: String
  error: String
}

type ReportsDiscoveryResponse {
  reports: [ReportEntity!]
}

type ReportsFilterOptionsResponse {
  values: [FilterOption!]
  filters: [FilterOptionNew!]
}

type ReportColumn {
  value: String!
  count: Float!
}

type CurrentPipelineReport implements CaseReportsColumns {
  id: ID!
  view_by: String!
  LEAD: ReportColumn!
  PRE_RECOMMENDATION: ReportColumn!
  PRE_APPLICATION: ReportColumn!
  APPLICATION_SUBMITTED: ReportColumn!
  AWAITING_VALUATION: ReportColumn!
  AWAITING_OFFER: ReportColumn!
  OFFER_RECEIVED: ReportColumn!
  EXCHANGE: ReportColumn!
  COMPLETE: ReportColumn!
}

type CancellationReasonReport implements CancellationReasonColumns {
  id: ID!
  view_by: String!
  NO_RESPONSE: ReportColumn!
  CLIENT_DECLINED_PRODUCT: ReportColumn!
  NO_PRODUCT_AVAILABLE: ReportColumn!
  INCORRECT_CONTACT_DETAILS: ReportColumn!
  CLIENT_NO_LONGER_REQUIRES_PRODUCT: ReportColumn!
  CLIENT_OBTAINED_PRODUCT_ELSEWHERE: ReportColumn!
  OTHER: ReportColumn!
  TOTAL: ReportColumn!
}

type RevenueReport {
  revenues: JSON
}

type RevenueTotals {
  totals: JSON
}

interface CaseReportsColumns {
  LEAD: ReportColumn!
  PRE_RECOMMENDATION: ReportColumn!
  PRE_APPLICATION: ReportColumn!
  APPLICATION_SUBMITTED: ReportColumn!
  AWAITING_VALUATION: ReportColumn!
  AWAITING_OFFER: ReportColumn!
  OFFER_RECEIVED: ReportColumn!
  EXCHANGE: ReportColumn!
  COMPLETE: ReportColumn!
}

interface CancellationReasonColumns {
  NO_RESPONSE: ReportColumn!
  CLIENT_DECLINED_PRODUCT: ReportColumn!
  NO_PRODUCT_AVAILABLE: ReportColumn!
  INCORRECT_CONTACT_DETAILS: ReportColumn!
  CLIENT_NO_LONGER_REQUIRES_PRODUCT: ReportColumn!
  CLIENT_OBTAINED_PRODUCT_ELSEWHERE: ReportColumn!
  OTHER: ReportColumn!
  TOTAL: ReportColumn
}

input ReportsReportQueueInput {
  reports: [ReportsInput]
}

input ReportsInput {
  report_id: String
  name: String
  description: String
  created_at: String
  key: String
  status: String
  error: String
}

input ReportsRevenueInput {
  advisor_ids: [String!]
  case_status: [String!]
  case_types: [String!]
  end_date: String
  introducer_ids: [String!]
  ledger_created_at_end: String
  ledger_created_at_start: String
  ledger_due_date_start: String
  ledger_due_date_end: String
  ledger_type: [LedgerType!]
  lender_ids: [String!]
  payment_direction: PaymentDirection
  payment_start_date: String
  payment_end_date: String
  payment_status: ReportsPaymentStatus
  start_date: String
  view_by: ReportsViewBy
  view_by_date: ReportsViewDateBy
}

input ReportsCommissionsInput {
  period_ids: [String!]
  advisor_id: String
}

input ReportsCaseStatsFilterInput {
  advisor_ids: [String!]
  case_status: [String!]
  case_types: [String!]
  introducer_ids: [String!]
  lender_ids: [String!]
  org_id: String
  start_date: String
  end_date: String
  ledger_type: [LedgerType!]
  view_by: ReportsViewBy
  introducer_org_id: String
}

input ReportsIntroducerCaseStatsFilterInput {
  advisor_ids: [String!]
  case_status: [String!]
  case_types: [String!]
  introducer_ids: [String!]
  lender_ids: [String!]
  org_id: String
  start_date: String
  end_date: String
  ledger_type: [LedgerType!]
  view_by: ReportsViewBy
  introducer_org_id: String!
}

input ReportsFilterInput {
  name: String
  names: [String!]
  report: String
}
